import { getAnalyticsId } from '@shared/core/analytics/segment/getAnalyticsId';
import { getSessionId } from '@shared/core/analytics/segment/getTrackerIds';
import { withWindow } from '@shared/utils/withWindow';
import { addError } from '@shared/utils/logger';

type ClarityApi = {
  (command: 'identify', userId: string, sessionId?: string, pageId?: string, friendlyName?: string): void;
  (command: 'set', key: string, value: string | ReadonlyArray<string>): void;
  (command: 'upgrade', reason: string): void;
  /** Commands recorded before the tag script loads, which the tag replays once it is there. */
  q?: IArguments[];
};

declare global {
  interface Window {
    clarity?: ClarityApi;
  }
}

let hasIdentifiedWithClarity = false;

/**
 * Identifies the current user with Microsoft Clarity by sending our analytics ID.
 * This creates a bidirectional link between Clarity sessions and our analytics.
 *
 * Safe to call before the Clarity loader snippet has run, like `setClarityTag`. `ClarityService` calls this from a
 * mount effect, which runs before Helmet applies the snippet, so without the stub the identify would be dropped.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/identify-api
 */
export const identifyWithClarity = (): void => {
  if (hasIdentifiedWithClarity) {
    return;
  }

  const analyticsId = getAnalyticsId();
  const sessionId = getSessionId();

  withWindow(globalWindow => {
    /**
     * NOTE: 'identify' tells Clarity to associate the provided analytics ID with the current session,
     * which is how we link user behavior back to the known identifier.
     * Pass our internal session ID to Clarity for additional correlation.
     * Note: This is our Joy session ID (Custom Session ID), not Clarity's own session ID.
     */
    try {
      const clarity = getClarityOrInstallQueueStub(globalWindow);
      clarity('identify', analyticsId, sessionId);
      hasIdentifiedWithClarity = true;
    } catch (error) {
      addError(error, { context: 'identifyWithClarity#identifyFailed' });
    }
  });
};

/**
 * Installs the same queue stub `ClarityLoaderScript`'s loader snippet installs: a function that pushes each call's
 * arguments onto `window.clarity.q`. The snippet keeps a stub it finds (it returns early only for the loaded tag,
 * and writes `c[a] = c[a] || ...`) and still injects the tag script, which replays `q`. Like the snippet, the stub
 * reads `window.clarity` at call time, so a later replacement still queues onto the live object.
 */
const installClarityQueueStub = (globalWindow: Window): ClarityApi => {
  const stub = function () {
    const installed = globalWindow.clarity;
    if (installed) {
      // eslint-disable-next-line prefer-rest-params
      (installed.q = installed.q || []).push(arguments);
    }
  } as ClarityApi;
  stub.q = stub.q || [];
  globalWindow.clarity = stub;
  return stub;
};

/** Returns the live `window.clarity`, or installs the queue stub when the loader snippet has not run yet. */
const getClarityOrInstallQueueStub = (globalWindow: Window): ClarityApi => {
  return typeof globalWindow.clarity === 'function' ? globalWindow.clarity : installClarityQueueStub(globalWindow);
};

/**
 * Sets a custom tag on the current Clarity session.
 * Tags appear as filterable dimensions in the Clarity dashboard.
 *
 * Safe to call before the Clarity loader snippet has run: the tag is queued on a stub this installs, and the
 * loader's tag script replays the queue. Without that, a tag set in the frame before Helmet applies the
 * snippet — which happens on a client-side navigation into a recorded route — would be dropped silently.
 *
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/cookie-consent#set
 */
export const setClarityTag = (key: string, value: string): void => {
  withWindow(globalWindow => {
    try {
      const clarity = getClarityOrInstallQueueStub(globalWindow);
      clarity('set', key, value);
    } catch (error) {
      addError(error, { context: 'setClarityTag#setFailed' });
    }
  });
};

/**
 * Asks Clarity to prioritize the current session's recording when it samples above the daily limit. It does not promise to keep it.
 * Safe to call before the Clarity loader snippet has run, like `setClarityTag`.
 *
 * @see https://learn.microsoft.com/en-us/clarity/clarity-api
 */
export const upgradeClaritySession = (reason: string): void => {
  withWindow(globalWindow => {
    try {
      const clarity = getClarityOrInstallQueueStub(globalWindow);
      clarity('upgrade', reason);
    } catch (error) {
      addError(error, { context: 'upgradeClaritySession#upgradeFailed', reason });
    }
  });
};
